import { getPromotionLineStockCap } from "./promotionLineStock";

export const resolveLineOrderType = (cartItem) =>
  cartItem?.order_type ||
  cartItem?.piece_type ||
  cartItem?.type ||
  "Box";

export const resolveCartLinePromoId = (line) => {
  const raw =
    line?.promo_id ??
    line?.promosId ??
    line?.promoId ??
    line?.promotion_id;
  if (raw === undefined || raw === null || raw === "") {
    return null;
  }
  return String(raw);
};

const promotionNameForGroupKey = (line) =>
  String(line?.promotion_name ?? line?.promotionName ?? line?.promo_name ?? "")
    .trim()
    .toLowerCase();

export const resolveCartLinePromoGroupKey = (line) => {
  const name = promotionNameForGroupKey(line);
  if (name) return `name:${name}`;
  const pid = resolveCartLinePromoId(line);
  if (pid) return `id:${pid}`;
  return "";
};

const inferBuyQuantityFromPromotionNameOnLine = (line) => {
  const name = String(
    line?.promotion_name ?? line?.promotionName ?? line?.promo_name ?? ""
  ).trim();
  let match = name.match(/\bBUY\s+(\d+)\b/i);
  if (match) {
    const n = parseInt(match[1], 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
  match = name.match(/\b(\d+)\s+FOR\s+(?:£|\$|€)?/i);
  if (match) {
    const n = parseInt(match[1], 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
  return 0;
};

export function resolvePromotionGroupKeyForCart(
  cartItems,
  promotionIdStr,
  referenceLine = null
) {
  if (referenceLine) {
    const refKey = resolveCartLinePromoGroupKey(referenceLine);
    if (refKey) return refKey;
  }
  if (!Array.isArray(cartItems) || !promotionIdStr) return "";
  for (const line of cartItems) {
    if (resolveCartLinePromoId(line) === promotionIdStr) {
      const key = resolveCartLinePromoGroupKey(line);
      if (key) return key;
    }
  }
  return `id:${promotionIdStr}`;
}

function cartLineBelongsToPromotionGroup(
  line,
  groupKey,
  promotionIdStr,
  promotionDetails,
  catalogTab = "any"
) {
  const lineGroup = resolveCartLinePromoGroupKey(line);
  if (groupKey && lineGroup && lineGroup === groupKey) return true;
  if (promotionIdStr && resolveCartLinePromoId(line) === promotionIdStr) {
    return true;
  }

  const linePromoId = resolveCartLinePromoId(line);
  if (lineGroup || linePromoId) {
    return false;
  }

  const productId = String(line.product_id ?? line.productId ?? "");
  if (!productId || !promotionDetails) return false;

  if (catalogTab === "buy" || catalogTab === "any") {
    const inBuy = (promotionDetails.buy_products || []).some(
      (p) => String(p.product_id) === productId
    );
    if (inBuy) return true;
  }

  return false;
}

const isCartLineBuyPoolContributor = (line, promotionDetails, promotionIdStr) => {
  const promoType = resolveCartLinePromoType(line);
  if (promoType === "GET" || promoType === "FREE") return false;
  if (isCartLineGetOrFreeRole(line)) return false;

  const productId = String(line.product_id ?? line.productId ?? "");
  const inBuyCatalog =
    productId &&
    (promotionDetails?.buy_products || []).some(
      (p) => String(p.product_id) === productId
    );
  const isPromoted = isCartLinePromoted(line);
  const matchesPromoId =
    promotionIdStr && resolveCartLinePromoId(line) === promotionIdStr;

  if (inBuyCatalog) {
    return !isCartLineGetOrFreeRole(line) &&
      (!isPromoted || matchesPromoId || promoType === "BUY");
  }

  if (promoType === "BUY") {
    const qty = parseFloat(pickCartLineQtyRaw(line));
    return Number.isFinite(qty) && qty > 0;
  }
  if (!isPromoted) return false;
  const qty = parseFloat(pickCartLineQtyRaw(line));
  return Number.isFinite(qty) && qty > 0;
};

export function resolvePromotionBuyPoolTotalQtyFromCartGroup(
  cartItems,
  groupKey,
  promotionDetails = null,
  promotionIdStr = ""
) {
  if (!Array.isArray(cartItems)) return 0;
  let total = 0;
  cartItems.forEach((line) => {
    if (
      !cartLineBelongsToPromotionGroup(
        line,
        groupKey,
        promotionIdStr,
        promotionDetails,
        "buy"
      )
    ) {
      return;
    }
    if (!isCartLineBuyPoolContributor(line, promotionDetails, promotionIdStr)) {
      return;
    }
    const qty = parseFloat(pickCartLineQtyRaw(line));
    if (Number.isFinite(qty) && qty > 0) total += qty;
  });
  return total;
}

export function resolvePromotionGetPoolTotalQtyFromCartGroup(
  cartItems,
  groupKey,
  promotionDetails = null,
  promotionIdStr = ""
) {
  if (!Array.isArray(cartItems)) return 0;
  let total = 0;
  cartItems.forEach((line) => {
    if (
      !cartLineBelongsToPromotionGroup(
        line,
        groupKey,
        promotionIdStr,
        promotionDetails,
        "get"
      )
    ) {
      return;
    }
    const promoType = resolveCartLinePromoType(line);
    const isGetLine =
      promoType === "GET" ||
      promoType === "FREE" ||
      isCartLineGetOrFreeRole(line);
    if (!isGetLine) return;
    const qty = parseFloat(pickCartLineQtyRaw(line));
    if (Number.isFinite(qty) && qty > 0) total += qty;
  });
  return total;
}

export function resolvePromotionGetProductQtyInCart(
  cartItems,
  productId,
  promotionDetails,
  promotionIdStr,
  referenceCartItem = null
) {
  const pid = String(productId ?? "");
  if (!pid || !Array.isArray(cartItems)) return 0;
  const groupKey = resolvePromotionGroupKeyForCart(
    cartItems,
    promotionIdStr,
    referenceCartItem
  );
  let total = 0;
  cartItems.forEach((line) => {
    if (String(line.product_id ?? line.productId ?? "") !== pid) return;
    if (
      !cartLineBelongsToPromotionGroup(
        line,
        groupKey,
        promotionIdStr,
        promotionDetails,
        "get"
      )
    ) {
      return;
    }
    const promoType = resolveCartLinePromoType(line);
    const isGetLine =
      promoType === "GET" ||
      promoType === "FREE" ||
      isCartLineGetOrFreeRole(line);
    if (!isGetLine) return;
    const qty = parseFloat(pickCartLineQtyRaw(line));
    if (Number.isFinite(qty) && qty > 0) total += Math.floor(qty);
  });
  return total;
}

export function resolveCompletedSetsFromBundleDiscounts(
  bundleDiscounts,
  promotionIdStr
) {
  if (!promotionIdStr) return 0;
  const bundle = (Array.isArray(bundleDiscounts) ? bundleDiscounts : []).find(
    (entry) => String(entry?.promotion_id ?? "").trim() === String(promotionIdStr)
  );
  const sets = parseInt(bundle?.sets_count ?? 0, 10);
  return Number.isFinite(sets) && sets > 0 ? sets : 0;
}

function resolvePromotionBuyQuantityFromContext(
  promotionDetails,
  cartItems,
  groupKey
) {
  const fromApi = parseInt(promotionDetails?.buy_quantity || 0, 10);
  if (fromApi > 0) return fromApi;
  if (!Array.isArray(cartItems) || !groupKey) return 0;
  for (const line of cartItems) {
    if (resolveCartLinePromoGroupKey(line) !== groupKey) continue;
    for (const field of [
      line.buy_quantity,
      line.buyQuantity,
      line.promotion_buy_quantity,
      line.promotionBuyQuantity,
    ]) {
      const bq = parseInt(field ?? 0, 10);
      if (bq > 0) return bq;
    }
    const inferred = inferBuyQuantityFromPromotionNameOnLine(line);
    if (inferred > 0) return inferred;
  }
  return 0;
}

export const resolveCartLinePromoType = (line) => {
  let t = String(
    line.promo_type ?? line.promoType ?? line.promosType ?? ""
  )
    .trim()
    .toUpperCase();
  if (!t && line.buyOrGet) {
    const b = String(line.buyOrGet).trim().toUpperCase();
    if (b === "BUY" || b === "GET" || b === "FREE") {
      t = b;
    }
  }
  return t;
};

export const isCartLinePromoted = (line) =>
  line.is_promoted === "1" ||
  line.is_promoted === 1 ||
  line.is_promoted === true ||
  line.promotion_applied === true ||
  line.promotion_applied === "1";

export const isCartLinePromotionAppliedFlag = (line) =>
  line?.promotion_applied === true ||
  line?.promotion_applied === "true" ||
  line?.promotion_applied === 1 ||
  line?.promotion_applied === "1";

export const isCartLinePromotedFlag = (line) =>
  line?.is_promoted === "1" ||
  line?.is_promoted === 1 ||
  line?.is_promoted === true;

/** Blue product name / P-CP qty controls — not paid buy rows that share a SKU with a free line. */
export const isCartLineOfferDisplayRole = (line) => {
  if (!line) return false;
  const promoType = resolveCartLinePromoType(line);
  if (promoType === "BUY") return false;

  const generated =
    line.generated === true || line.generated === "1" || line.generated === 1;
  if (generated) return true;

  const price = Math.max(
    parseFloat(line.price ?? NaN) || 0,
    parseFloat(line.unit_price ?? NaN) || 0,
    parseFloat(line.promo_unit_price ?? NaN) || 0
  );
  const hasPaidPrice = Number.isFinite(price) && price > 0.0001;
  if (hasPaidPrice && promoType !== "GET" && promoType !== "FREE") {
    return false;
  }

  const promotionApplied = isCartLinePromotionAppliedFlag(line);
  const isPromoted = isCartLinePromotedFlag(line);

  if (promoType === "GET" || promoType === "FREE") {
    return promotionApplied || isPromoted || !hasPaidPrice;
  }

  const freeFlag = String(line.free_product ?? "").trim().toUpperCase() === "Y";
  if ((promotionApplied || isPromoted) && (freeFlag || !hasPaidPrice)) {
    return true;
  }

  return false;
};

/** GET/FREE/generated lines — not paid buy rows that share a SKU with a free line. */
export const isCartLineGetOrFreeRole = (line) => isCartLineOfferDisplayRole(line);

export const cartLinePaidOfferScopeKey = (item) => {
  if (!item) return "";
  const pid = String(item.product_id ?? item.productId ?? "");
  const type = String(item.order_type || item.type || item.piece_type || "Box")
    .trim()
    .toLowerCase();
  const role = isCartLineOfferDisplayRole(item) ? "offer" : "paid";
  return `${pid}|${type}|${role}`;
};

export const isSameCartPaidOfferLine = (item, target) => {
  if (!item || !target) return false;
  const a = cartLinePaidOfferScopeKey(item);
  const b = cartLinePaidOfferScopeKey(target);
  return Boolean(a) && a === b;
};

export const CART_ITEM_LOOKUP_ROLE_PAID = "paid";
export const CART_ITEM_LOOKUP_ROLE_OFFER = "offer";
export const CART_ITEM_LOOKUP_ROLE_GET = "get";
export const CART_ITEM_LOOKUP_ROLE_ANY = "any";

const EMPTY_CART_ITEM_LOOKUP = Object.freeze({
  byKey: new Map(),
  indexByKey: new Map(),
});

const cartItemLookupByItems = new WeakMap();

export const buildCartItemCompositeKey = (
  productId,
  orderType,
  role = CART_ITEM_LOOKUP_ROLE_ANY
) =>
  `${String(productId ?? "")}|${normalizePromotionOrderType(orderType)}|${role || CART_ITEM_LOOKUP_ROLE_ANY}`;

const resolveCartItemLookupProductId = (item) =>
  String(item?.product_id ?? item?.productId ?? "");

export const buildCartItemLookupMap = (cartItems) => {
  const byKey = new Map();
  const indexByKey = new Map();
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return { byKey, indexByKey };
  }
  const setFirst = (key, item, index) => {
    if (!byKey.has(key)) {
      byKey.set(key, item);
      indexByKey.set(key, index);
    }
  };
  for (let i = 0; i < cartItems.length; i += 1) {
    const item = cartItems[i];
    if (!item) continue;
    const productId = resolveCartItemLookupProductId(item);
    if (!productId) continue;
    const orderType = resolveLineOrderType(item);
    const anyKey = buildCartItemCompositeKey(
      productId,
      orderType,
      CART_ITEM_LOOKUP_ROLE_ANY
    );
    const role = isCartLineGetOrFreeRole(item)
      ? CART_ITEM_LOOKUP_ROLE_OFFER
      : CART_ITEM_LOOKUP_ROLE_PAID;
    const roleKey = buildCartItemCompositeKey(productId, orderType, role);
    setFirst(anyKey, item, i);
    setFirst(roleKey, item, i);
    const promoType = resolveCartLinePromoType(item);
    if (
      isCartLinePromoted(item) &&
      (promoType === "GET" ||
        promoType === "FREE" ||
        isCartLineGetOrFreeRole(item))
    ) {
      setFirst(
        buildCartItemCompositeKey(
          productId,
          orderType,
          CART_ITEM_LOOKUP_ROLE_GET
        ),
        item,
        i
      );
    }
  }
  return { byKey, indexByKey };
};

export const getCartItemLookupMap = (cartItems) => {
  if (!Array.isArray(cartItems)) return EMPTY_CART_ITEM_LOOKUP;
  if (cartItems.length === 0) return EMPTY_CART_ITEM_LOOKUP;
  const cached = cartItemLookupByItems.get(cartItems);
  if (cached) return cached;
  const built = buildCartItemLookupMap(cartItems);
  cartItemLookupByItems.set(cartItems, built);
  return built;
};

const resolveLookupSource = (cartItemsOrLookup) => {
  if (!cartItemsOrLookup) return EMPTY_CART_ITEM_LOOKUP;
  if (cartItemsOrLookup.byKey && cartItemsOrLookup.indexByKey) {
    return cartItemsOrLookup;
  }
  return getCartItemLookupMap(cartItemsOrLookup);
};

export const getCartItemFromLookup = (
  cartItemsOrLookup,
  productId,
  orderType,
  role = CART_ITEM_LOOKUP_ROLE_ANY
) => {
  const pid = String(productId ?? "");
  if (!pid) return null;
  const lookup = resolveLookupSource(cartItemsOrLookup);
  return (
    lookup.byKey.get(buildCartItemCompositeKey(pid, orderType, role)) ?? null
  );
};

export const getCartItemIndexFromLookup = (
  cartItemsOrLookup,
  productId,
  orderType,
  role = CART_ITEM_LOOKUP_ROLE_ANY
) => {
  const pid = String(productId ?? "");
  if (!pid) return -1;
  const lookup = resolveLookupSource(cartItemsOrLookup);
  const idx = lookup.indexByKey.get(
    buildCartItemCompositeKey(pid, orderType, role)
  );
  return idx === undefined ? -1 : idx;
};

/** Stock / purchase-limit totals must not mix buy rows with GET/FREE rows for the same SKU. */
export const cartLinesShareBuyQtyValidationBucket = (line, referenceLine) => {
  if (!line || !referenceLine) return false;
  return (
    isCartLineGetOrFreeRole(line) === isCartLineGetOrFreeRole(referenceLine)
  );
};

export const normalizePromotionOrderType = (orderType) => {
  const s = String(orderType ?? "Box").trim().toLowerCase();
  return s === "piece" ? "Piece" : "Box";
};

export const resolveOfferProductOrderType = (row, fallback = "Box") =>
  normalizePromotionOrderType(
    row?.product_type ??
      row?.piece_type ??
      row?.order_type ??
      fallback
  );

export const resolveOfferProductStockQty = (row, fallbackOrderType = "Box") => {
  const cap = getPromotionLineStockCap(
    row,
    resolveOfferProductOrderType(row, fallbackOrderType)
  );
  if (cap === null || !Number.isFinite(cap)) {
    return null;
  }
  return Math.max(0, Math.floor(cap));
};

export const isProductInBuyCatalog = (promoDetails, productId) => {
  const pid = String(productId ?? "");
  if (!pid || !promoDetails) return false;
  return (promoDetails.buy_products || []).some(
    (p) => String(p.product_id) === pid
  );
};

export const findPromotionCartLine = (cartItems, { productId, orderType, tabType }) => {
  if (tabType === "get") {
    return getCartItemFromLookup(
      cartItems,
      productId,
      orderType,
      CART_ITEM_LOOKUP_ROLE_GET
    );
  }
  if (tabType === "buy") {
    return getCartItemFromLookup(
      cartItems,
      productId,
      orderType,
      CART_ITEM_LOOKUP_ROLE_PAID
    );
  }
  return getCartItemFromLookup(
    cartItems,
    productId,
    orderType,
    CART_ITEM_LOOKUP_ROLE_ANY
  );
};

export const findPromotionCartLineIndex = (
  cartItems,
  { productId, orderType, tabType }
) => {
  if (tabType === "get") {
    return getCartItemIndexFromLookup(
      cartItems,
      productId,
      orderType,
      CART_ITEM_LOOKUP_ROLE_GET
    );
  }
  if (tabType === "buy") {
    return getCartItemIndexFromLookup(
      cartItems,
      productId,
      orderType,
      CART_ITEM_LOOKUP_ROLE_PAID
    );
  }
  return getCartItemIndexFromLookup(
    cartItems,
    productId,
    orderType,
    CART_ITEM_LOOKUP_ROLE_ANY
  );
};

export const isCartLineMergeableOnProductAdd = (item) =>
  !isCartLineGetOrFreeRole(item);

export const findMergeableCartLineForProductAdd = (
  cartItems,
  productId,
  orderType
) =>
  getCartItemFromLookup(
    cartItems,
    productId,
    orderType,
    CART_ITEM_LOOKUP_ROLE_PAID
  );

/** Drop swapped-out GET/FREE lines so check-promotion can rebuild offer rows from the server. */
export const stripRemovedGetLinesFromCart = (cartItems, removedProducts) => {
  if (!Array.isArray(cartItems)) return [];
  if (!Array.isArray(removedProducts) || removedProducts.length === 0) {
    return cartItems;
  }
  return cartItems.filter((item) => {
    if (!isCartLineGetOrFreeRole(item)) return true;
    const pid = String(item.product_id ?? item.productId ?? "");
    const orderType = normalizePromotionOrderType(resolveLineOrderType(item));
    const isRemoved = removedProducts.some(
      (r) =>
        String(r.product_id) === pid &&
        normalizePromotionOrderType(r.product_type) === orderType
    );
    return !isRemoved;
  });
};

export const ensureGetLineRemovedBeforeReplace = ({
  removedProducts,
  newProducts,
  existingCartQuantities,
  promoDetails,
}) => {
  const removed = Array.isArray(removedProducts)
    ? removedProducts.map((r) => ({ ...r }))
    : [];
  if (!Array.isArray(newProducts) || !promoDetails) return removed;

  newProducts.forEach((added) => {
    const pid = String(added?.product_id ?? "");
    if (!pid || !isProductInBuyCatalog(promoDetails, pid)) return;
    const pType = normalizePromotionOrderType(added.product_type);
    const getKey = `get_${pid}_${pType}`;
    const previousGetQty = existingCartQuantities?.[getKey] || 0;
    if (previousGetQty <= 0) return;

    const alreadyRemoved = removed.some(
      (r) =>
        String(r.product_id) === pid &&
        normalizePromotionOrderType(r.product_type) === pType
    );
    if (alreadyRemoved) return;

    removed.push({
      product_id: added.product_id,
      product_type: pType,
      qty: previousGetQty,
    });
  });

  return removed;
};

export const pickCartLineQtyRaw = (line) =>
  line
    ? [line.allot_qty, line.qty, line.quantity].find(
        (v) => v !== undefined && v !== null && v !== ""
      )
    : undefined;

export const resolveRemovedQty = (cartItem) => {
  const n = parseFloat(pickCartLineQtyRaw(cartItem));
  return Number.isFinite(n) ? n : 0;
};

export const getPromotionOfferProductUnitPrice = (product, pricingType) => {
  if (pricingType === "Box") {
    const offer = parseFloat(
      product?.box_offer_price || product?.offer_price || 0
    );
    const sales = parseFloat(
      product?.box_sales_price || product?.sales_price || 0
    );
    return offer > 0 ? offer : sales;
  }
  const offer = parseFloat(
    product?.piece_offer_price || product?.offer_price || 0
  );
  const sales = parseFloat(
    product?.piece_sales_price || product?.sales_price || 0
  );
  return offer > 0 ? offer : sales;
};

/**
 * @param {object} [options]
 * @param {boolean} [options.eposOfferProductLogic] When true, GET/FREE rows also use Edit EPOS
 *   `isGetProductInPromotion` (GET/FREE type + product in get_products). Quick order keeps the
 *   original rule: offer lines only when promoted, matching this promotion, and GET/FREE.
 */
export function buildPromotionCartQuantityMaps(
  cartItems,
  promoDetails,
  promotionIdStr,
  options = {}
) {
  const existingQuantities = {};
  const pricingTypes = {};
  const eposOfferProductLogic = options.eposOfferProductLogic === true;
  if (!Array.isArray(cartItems) || !promoDetails || !promotionIdStr) {
    return { existingQuantities, pricingTypes };
  }

  const buyCatalog = promoDetails?.buy_products || [];
  const getCatalog = promoDetails?.get_products || [];

  cartItems.forEach((line) => {
    const cartPromoId = resolveCartLinePromoId(line);
    const productId = String(line.product_id ?? line.productId ?? "");
    if (!productId) {
      return;
    }

    const isPromoted = isCartLinePromoted(line);
    const matchesPromoId = cartPromoId === promotionIdStr;
    const promoType = resolveCartLinePromoType(line);
    const orderType =
      line.order_type || line.piece_type || line.type || "Box";

    const inBuyCatalog = buyCatalog.some(
      (p) => String(p.product_id) === productId
    );
    const inGetCatalog = getCatalog.some(
      (p) => String(p.product_id) === productId
    );

    const isGetOrFreeType = promoType === "GET" || promoType === "FREE";
    const isOfferDisplayLine = isCartLineGetOrFreeRole(line);

    const isOfferTabQuick =
      isPromoted && matchesPromoId && isGetOrFreeType;
    const isOfferTab = eposOfferProductLogic
      ? isOfferDisplayLine &&
        matchesPromoId &&
        (inGetCatalog || isPromoted || isGetOrFreeType)
      : isOfferTabQuick;

    let tabType = null;

    if (isOfferTab) {
      tabType = "get";
    } else if (
      inBuyCatalog &&
      !isOfferDisplayLine &&
      (!isPromoted || matchesPromoId || promoType === "BUY")
    ) {
      // Buy-tab qty: same product on a normal line, this promotion, or another promotion's BUY line
      tabType = "buy";
    }

    if (!tabType) {
      return;
    }

    const uniqueKey = `${tabType}_${productId}_${orderType}`;
    const qty = parseFloat(pickCartLineQtyRaw(line));
    const safeQty = Number.isFinite(qty) ? qty : 0;
    existingQuantities[uniqueKey] =
      (existingQuantities[uniqueKey] || 0) + safeQty;
    if (!pricingTypes[uniqueKey]) {
      pricingTypes[uniqueKey] = orderType;
    }
  });
  return { existingQuantities, pricingTypes };
}

export function buildExistingCartQuantities(
  cartItems,
  promoDetails,
  promotionIdStr,
  options = {}
) {
  return buildPromotionCartQuantityMaps(
    cartItems,
    promoDetails,
    promotionIdStr,
    options
  ).existingQuantities;
}

export function resolvePromotionBuyPoolTotalQtyFromCart(
  cartItems,
  promotionIdStr,
  promotionDetails = null
) {
  if (!Array.isArray(cartItems) || !promotionIdStr || !promotionDetails) return 0;

  const buyCatalog = promotionDetails?.buy_products || [];
  const buyCatalogIds = new Set(
    buyCatalog.map((p) => String(p.product_id))
  );
  const useBuyCatalogFilter = buyCatalogIds.size > 0;

  let total = 0;
  cartItems.forEach((line) => {
    const cartPromoId = resolveCartLinePromoId(line);
    const productId = String(line.product_id ?? line.productId ?? "");
    if (!productId) return;

    const isPromoted = isCartLinePromoted(line);
    const matchesPromoId = cartPromoId === promotionIdStr;
    const promoType = resolveCartLinePromoType(line);
    const isOfferDisplayLine = isCartLineGetOrFreeRole(line);

    if (promoType === "GET" || promoType === "FREE" || isOfferDisplayLine) {
      return;
    }

    const inBuyCatalog = useBuyCatalogFilter
      ? buyCatalogIds.has(productId)
      : matchesPromoId;

    if (!inBuyCatalog) return;

    if (
      !useBuyCatalogFilter &&
      !matchesPromoId &&
      promoType !== "BUY" &&
      isPromoted
    ) {
      return;
    }

    if (
      useBuyCatalogFilter &&
      !(
        !isOfferDisplayLine &&
        (!isPromoted || matchesPromoId || promoType === "BUY")
      )
    ) {
      return;
    }

    const qty = parseFloat(pickCartLineQtyRaw(line));
    if (Number.isFinite(qty) && qty > 0) total += qty;
  });

  return total;
}

export function resolvePromotionBuyPoolTotalQty(
  existingCartQuantities,
  productQuantities = {}
) {
  let totalBuyQty = 0;
  Object.keys(existingCartQuantities || {})
    .filter((key) => key.startsWith("buy_"))
    .forEach((key) => {
      const existingQty = existingCartQuantities[key] || 0;
      const uiQty = productQuantities[key];
      if (uiQty !== undefined && uiQty !== null) {
        totalBuyQty += uiQty;
      } else {
        totalBuyQty += existingQty;
      }
    });
  Object.keys(productQuantities || {})
    .filter(
      (key) =>
        key.startsWith("buy_") && existingCartQuantities?.[key] === undefined
    )
    .forEach((key) => {
      totalBuyQty += productQuantities[key] || 0;
    });
  return totalBuyQty;
}

export function resolveCompletedPromotionSets(
  promotionDetails,
  existingCartQuantities,
  productQuantities = {},
  options = {}
) {
  const {
    cartItems = null,
    promotionIdStr = "",
    referenceCartItem = null,
    bundleDiscounts = null,
  } = options;

  const groupKey =
    Array.isArray(cartItems) && promotionIdStr
      ? resolvePromotionGroupKeyForCart(
          cartItems,
          promotionIdStr,
          referenceCartItem
        )
      : referenceCartItem
        ? resolveCartLinePromoGroupKey(referenceCartItem)
        : promotionIdStr
          ? `id:${promotionIdStr}`
          : "";

  const buyQuantity = resolvePromotionBuyQuantityFromContext(
    promotionDetails,
    cartItems,
    groupKey
  );

  const candidates = [];

  if (buyQuantity > 0) {
    const mapQty = resolvePromotionBuyPoolTotalQty(
      existingCartQuantities,
      productQuantities
    );
    if (mapQty > 0) {
      candidates.push(Math.floor(mapQty / buyQuantity));
    }

    if (Array.isArray(cartItems) && cartItems.length > 0 && promotionIdStr) {
      const fromCartId = resolvePromotionBuyPoolTotalQtyFromCart(
        cartItems,
        promotionIdStr,
        promotionDetails
      );
      if (fromCartId > 0) {
        candidates.push(Math.floor(fromCartId / buyQuantity));
      }
    }

    if (Array.isArray(cartItems) && groupKey) {
      const fromCartGroup = resolvePromotionBuyPoolTotalQtyFromCartGroup(
        cartItems,
        groupKey,
        promotionDetails,
        promotionIdStr
      );
      if (fromCartGroup > 0) {
        candidates.push(Math.floor(fromCartGroup / buyQuantity));
      }
    }
  }

  const bundleSets = resolveCompletedSetsFromBundleDiscounts(
    bundleDiscounts,
    promotionIdStr
  );
  if (bundleSets > 0) candidates.push(bundleSets);

  if (candidates.length === 0) return 0;
  return Math.max(...candidates);
}

export function isCompulsoryOfferProduct(product) {
  const v = product?.is_compulsory;
  return v === "1" || v === 1 || v === true;
}

export function resolveMandatoryGetMinQuantity(
  product,
  promotionDetails,
  existingCartQuantities,
  productQuantities = {},
  options = {}
) {
  if (!isCompulsoryOfferProduct(product)) return 0;
  const perSet = parseInt(product?.compulsory_quantity || 0, 10);
  const minPerSet = perSet > 0 ? perSet : 1;
  const sets = resolveCompletedPromotionSets(
    promotionDetails,
    existingCartQuantities,
    productQuantities,
    options
  );
  return Math.max(sets, 1) * minPerSet;
}

export function calculateMaxAllowedGetQuantity(
  promotionDetails,
  existingCartQuantities,
  productQuantities = {}
) {
  const getQuantity = parseInt(promotionDetails?.get_quantity || 0, 10);
  const buyQuantity = parseInt(promotionDetails?.buy_quantity || 0, 10);
  const totalExistingGetQty = Object.keys(existingCartQuantities || {})
    .filter((key) => key.startsWith("get_"))
    .reduce((sum, key) => sum + (existingCartQuantities[key] || 0), 0);
  const hasBuyProductsMeta =
    Array.isArray(promotionDetails?.buy_products) &&
    promotionDetails.buy_products.length > 0;
  if (!hasBuyProductsMeta) {
    return Math.max(getQuantity, totalExistingGetQty);
  }
  if (buyQuantity <= 0 || getQuantity <= 0) {
    return Math.max(getQuantity, totalExistingGetQty);
  }
  const totalBuyQty = resolvePromotionBuyPoolTotalQty(
    existingCartQuantities,
    productQuantities
  );
  if (totalBuyQty <= 0) {
    return Math.max(getQuantity, totalExistingGetQty);
  }
  const sets = Math.floor(totalBuyQty / buyQuantity);
  return Math.max(sets * getQuantity, totalExistingGetQty);
}

export function sumGetQuantities(existingQuantities) {
  return Object.keys(existingQuantities)
    .filter((key) => key.startsWith("get_"))
    .reduce((sum, key) => sum + (existingQuantities[key] || 0), 0);
}

export function sumExistingGetQtyForProduct(existingQuantities, productId) {
  const pid = String(productId ?? "");
  if (!pid) return 0;
  const prefix = `get_${pid}_`;
  return Object.keys(existingQuantities || {})
    .filter((key) => key.startsWith(prefix))
    .reduce((sum, key) => sum + (existingQuantities[key] || 0), 0);
}

export function sumUiGetQuantitiesForOtherProducts(uiQuantities, excludeProductId) {
  const exclude = String(excludeProductId ?? "");
  return Object.entries(uiQuantities || {}).reduce((sum, [pid, qty]) => {
    if (String(pid) === exclude) return sum;
    return sum + Math.max(0, Math.floor(Number(qty) || 0));
  }, 0);
}

export function computeQtyBounds({
  promoDetails,
  existingQuantities,
  cartItem,
  selectedProduct,
  orderType,
  uiQuantities = null,
  skipOfferStockLimit = true,
  cartItems = null,
  promotionIdStr = "",
  referenceCartItem = null,
  bundleDiscounts = null,
}) {
  const selectedPid = String(
    selectedProduct?.product_id ?? cartItem?.product_id ?? ""
  );
  const maxAllowedGet = calculateMaxAllowedGetQuantity(
    promoDetails,
    existingQuantities
  );
  const totalGet = sumGetQuantities(existingQuantities);
  let maxFromPromotionRule;
  if (uiQuantities && typeof uiQuantities === "object") {
    const totalOthers = sumUiGetQuantitiesForOtherProducts(
      uiQuantities,
      selectedPid
    );
    maxFromPromotionRule = Math.max(0, maxAllowedGet - totalOthers);
  } else {
    const existingForSelected = sumExistingGetQtyForProduct(
      existingQuantities,
      selectedPid
    );
    const removedQty =
      existingForSelected > 0
        ? Math.floor(existingForSelected)
        : Math.floor(resolveRemovedQty(cartItem));
    maxFromPromotionRule = Math.max(0, maxAllowedGet - totalGet + removedQty);
  }
  const removedQty = sumExistingGetQtyForProduct(existingQuantities, selectedPid) ||
    Math.floor(resolveRemovedQty(cartItem));
  const stockCap =
    skipOfferStockLimit === true
      ? null
      : selectedProduct != null
        ? getPromotionLineStockCap(selectedProduct, orderType)
        : null;
  const stockMax = stockCap != null ? Math.max(0, Math.floor(stockCap)) : null;
  let maxQ = maxFromPromotionRule;
  if (stockMax != null) {
    maxQ = Math.min(maxQ, stockMax);
  }
  maxQ = Math.max(0, Math.floor(maxQ));
  const mandatoryMin = resolveMandatoryGetMinQuantity(
    selectedProduct,
    promoDetails,
    existingQuantities,
    uiQuantities,
    { cartItems, promotionIdStr, referenceCartItem, bundleDiscounts }
  );
  const minQ = mandatoryMin > 0 ? mandatoryMin : 0;
  return {
    minQ,
    maxQ,
    maxAllowedGet,
    totalGet,
    removedQty,
    stockMax,
  };
}

export function toValidLimit(value) {
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
}

export function parsePromotionMinTotalGetQty(promoDetails) {
  const n = parseInt(String(promoDetails?.get_quantity ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function sumOtherGetQuantitiesExcludingRemoved(
  existingQuantities,
  cartItem,
  removedOrderType
) {
  const removedPid = String(cartItem?.product_id ?? "");
  let sum = 0;
  Object.keys(existingQuantities || {}).forEach((key) => {
    if (!key.startsWith("get_")) return;
    const parts = key.split("_");
    if (parts.length < 3) return;
    const productId = parts[1];
    const orderTypeFromKey = parts[2] || "Box";
    const isRemoved =
      String(productId) === removedPid &&
      orderTypeFromKey === removedOrderType;
    if (isRemoved) return;
    sum += existingQuantities[key] || 0;
  });
  return sum;
}

/** Build remove[] for change_get_product from cart GET lines vs dialog selection. */
export function buildRemovedGetProductsPayload(
  existingQuantities,
  requestedQuantities,
  options = {}
) {
  const { cartItem = null } = options;
  const removed = [];
  const seen = new Set();

  const pushRemoved = (productId, productType, qty) => {
    const pid = String(productId ?? "");
    const pType = normalizePromotionOrderType(productType);
    const q = Math.max(0, Math.floor(Number(qty) || 0));
    if (!pid || q <= 0) return;
    const dedupeKey = `${pid}_${pType}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    removed.push({ product_id: productId, product_type: pType, qty: q });
  };

  Object.keys(existingQuantities || {})
    .filter((key) => key.startsWith("get_"))
    .forEach((key) => {
      const parts = key.split("_");
      if (parts.length < 3) return;
      const productId = parts[1];
      const orderType = parts[2] || "Box";
      const existingQty = Math.max(
        0,
        Math.floor(Number(existingQuantities[key]) || 0)
      );
      if (existingQty <= 0) return;

      const requestedQty = Math.max(
        0,
        Math.floor(Number(requestedQuantities?.[productId] ?? 0) || 0)
      );

      if (requestedQty !== existingQty) {
        pushRemoved(productId, orderType, existingQty);
      }
    });

  if (cartItem) {
    const cartPid = String(cartItem.product_id ?? "");
    const cartOT = resolveLineOrderType(cartItem);
    const cartQty = Math.floor(resolveRemovedQty(cartItem));
    const requestedForCart = Math.max(
      0,
      Math.floor(Number(requestedQuantities?.[cartPid] ?? 0) || 0)
    );
    const hasNewSelection = Object.entries(requestedQuantities || {}).some(
      ([pid, qty]) =>
        Math.floor(Number(qty) || 0) > 0 && String(pid) !== cartPid
    );

    if (cartQty > 0 && (hasNewSelection || requestedForCart !== cartQty)) {
      pushRemoved(cartItem.product_id, cartOT, cartQty);
    }
  }

  return removed;
}

export function buildAllNewGetProductsPayload(
  selected,
  orderType,
  qty,
  existingQuantities,
  cartItem,
  getProductRows,
  requestedQuantities = null
) {
  if (requestedQuantities && typeof requestedQuantities === "object") {
    const removedOT = orderType;
    const allNew = [];
    (getProductRows || []).forEach((product) => {
      const pid = String(product?.product_id ?? "");
      if (!pid) return;
      const rawQty = requestedQuantities[pid] ?? 0;
      const nextQty = Math.max(0, Math.floor(Number(rawQty) || 0));
      if (nextQty <= 0) return;
      allNew.push({
        product_id: product.product_id,
        product_type: removedOT,
        qty: nextQty,
        new_product_unit_price: getPromotionOfferProductUnitPrice(
          product,
          removedOT
        ),
      });
    });
    return allNew;
  }
  const removedPid = String(cartItem?.product_id ?? "");
  const removedOT = orderType;
  const allNew = [
    {
      product_id: selected.product_id,
      product_type: orderType,
      qty,
      new_product_unit_price: getPromotionOfferProductUnitPrice(
        selected,
        orderType
      ),
    },
  ];
  Object.keys(existingQuantities || {})
    .filter((k) => k.startsWith("get_"))
    .forEach((key) => {
      const parts = key.split("_");
      if (parts.length < 3) return;
      const productId = parts[1];
      const orderTypeFromKey = parts[2] || "Box";
      const existingQty = existingQuantities[key] || 0;
      if (existingQty <= 0) return;
      if (String(productId) === removedPid && orderTypeFromKey === removedOT) {
        return;
      }
      const dupIdx = allNew.findIndex(
        (x) =>
          String(x.product_id) === String(productId) &&
          x.product_type === orderTypeFromKey
      );
      const product = getProductRows.find(
        (p) => String(p.product_id) === String(productId)
      );
      if (!product) return;
      if (dupIdx >= 0) {
        allNew[dupIdx] = {
          ...allNew[dupIdx],
          qty: allNew[dupIdx].qty + existingQty,
        };
        return;
      }
      allNew.push({
        product_id: product.product_id,
        product_type: orderTypeFromKey,
        qty: existingQty,
        new_product_unit_price: getPromotionOfferProductUnitPrice(
          product,
          orderTypeFromKey
        ),
      });
    });
  return allNew;
}

const normalizeCheckPromotionPieceType = (orderType) =>
  String(orderType ?? "box").trim().toLowerCase();

/** Map change_get_product payloads to check-promotion `changed_get_products`. */
export function buildChangedGetProductsForCheckPromotion(
  promotionId,
  removedProducts,
  newProducts
) {
  const promoId = parseInt(String(promotionId ?? ""), 10);
  if (!Number.isFinite(promoId) || promoId <= 0) {
    return [];
  }

  const remove = (Array.isArray(removedProducts) ? removedProducts : [])
    .map((row) => {
      const productId = parseInt(String(row?.product_id ?? ""), 10);
      if (!Number.isFinite(productId) || productId <= 0) return null;
      return {
        product_id: productId,
        piece_type: normalizeCheckPromotionPieceType(
          row?.product_type ?? row?.piece_type
        ),
      };
    })
    .filter(Boolean);

  const add = (Array.isArray(newProducts) ? newProducts : [])
    .map((row) => {
      const productId = parseInt(String(row?.product_id ?? ""), 10);
      const quantity = Math.max(
        0,
        Math.floor(Number(row?.qty ?? row?.quantity) || 0)
      );
      const unitPrice = parseFloat(
        row?.new_product_unit_price ?? row?.unit_price ?? 0
      );
      if (!Number.isFinite(productId) || productId <= 0 || quantity <= 0) {
        return null;
      }
      return {
        product_id: productId,
        piece_type: normalizeCheckPromotionPieceType(
          row?.product_type ?? row?.piece_type
        ),
        quantity,
        unit_price: Number.isFinite(unitPrice) ? unitPrice : 0,
      };
    })
    .filter(Boolean);

  if (remove.length === 0 && add.length === 0) {
    return [];
  }

  return [
    {
      promotion_id: promoId,
      remove,
      add,
    },
  ];
}
